import React from "react";
import clsx from "clsx";

import Text from "../components/common/Text/Text";

import * as strings from "../strings";
import { isFiveKeysTheme } from "./util/ThemeUtils";
import { HW_APP_TYPE } from "./consts/HardwareConstants";
import {
	IconDistraction,
	IconEco,
	IconFatigue,
	IconRisk,
	IconSpeed,
	IconHarshAcceleration,
	IconHarshBraking,
	ScoreBackground,
	ScoreCircles,
} from "../assets";

// TODO: Cleanup keys for pillar object

export const PILLAR_LEVEL_GROUP = "group";
export const PILLAR_LEVEL_ROOT = "root";
export const PILLAR_LEVEL_USER = "user";

const USE_NEW_PER_100 = false;

const per100KMOld = (value: any) =>
	(value.total / (value.mileage / 1000)) * 100;
const per100KMNew = (value: any) =>
	value.mileage < 100_000
		? value.total
		: (value.total / (value.mileage / 1000)) * 100;
const per100KM = USE_NEW_PER_100 ? per100KMNew : per100KMOld;

export const DRIVING_SCORE = {
	id: "drivingScore",
	calc: (value: any) => {
		return Math.round(value.total);
	},
	strings: {
		name: strings.SINGLE_SCORE_NAME,
		xAxisTitle: () => strings.SINGLE_SCORE_XAXIS_TITLE,
		tooltip: {
			title: strings.SINGLE_SCORE_TOOLTIP_TITLE,
			copy: (trainingEnabled?: boolean) =>
				isFiveKeysTheme()
					? trainingEnabled
						? strings.FIVE_KEYS_SCORE_TOOLTIP_COPY_TRAINING
						: strings.FIVE_KEYS_SCORE_TOOLTIP_COPY
					: strings.SINGLE_SCORE_TOOLTIP_COPY,
			fleet: strings.MISC_EMPTY_STRING,
			user: strings.MISC_EMPTY_STRING,
		},
	},
	data: {
		averageBy: "engineEvents",
		key: isFiveKeysTheme() ? "smithDrivingScore" : "drivingScore",
		stat: isFiveKeysTheme() ? "smithDrivingScore" : "drivingScore",
	},
	panelContent: (
		score?: number,
		mileage?: number,
		loading?: boolean,
		appType?: string,
	) => {
		const noTrips = (mileage || 0) < 1;
		const lowMileage = (mileage || 0) < (appType === HW_APP_TYPE ? 10 : 10_000);

		return (
			<div
				className={clsx("SingleScore__container", { "--skeleton": loading })}
			>
				<div className={"SingleScore__score-container"}>
					{isFiveKeysTheme() ? (
						<>
							<ScoreCircles />
							<Text
								className={clsx("SingleScore__score --circles", {
									"--no-trips": noTrips || lowMileage,
								})}
							>
								{!noTrips &&
									!lowMileage &&
									Math.min(Math.round(score || 0), 100)}
								{(noTrips || lowMileage) && "- -"}
							</Text>
						</>
					) : (
						<>
							<ScoreBackground />
							<Text
								className={clsx("SingleScore__score", {
									"--no-trips": noTrips || lowMileage,
								})}
							>
								{!noTrips &&
									!lowMileage &&
									Math.min(Math.round(score || 0), 100)}
								{noTrips && strings.MISC_NO_DATA_RECORDED}
								{lowMileage && !noTrips && strings.MISC_NOT_ENOUGH_MILEAGE}
							</Text>
						</>
					)}
				</div>
				<Text className={"SingleScoreChart__score-title"}>
					{strings.SINGLE_SCORE_CONTENT_TITLE}
				</Text>
			</div>
		);
	},
};

export const PILLAR_RISK = {
	type: "risk",
	id: "risk",
	icon: IconRisk,
	calc: per100KM,
	tooltipIndex: 0,
	strings: {
		name: strings.PILLAR_RISK_NAME,
		benchmarkTooltip: strings.PILLAR_BENCHMARK_TOOLTIP_EVENTS,
		description: (imperial: boolean) =>
			strings.PILLAR_RISK_DESCRIPTION.with({
				unit: imperial
					? strings.UNIT_62_MILES_SHORT
					: strings.UNIT_100_KILOMETERS_SHORT,
			}),
		unitPlural: strings.PILLAR_UNIT_EVENT_PLURAL,
		unit: strings.PILLAR_UNIT_EVENT,
		suffix: "",
		fullName: strings.PILLAR_RISK_FULL_NAME,
		fullNamePlural: strings.PILLAR_RISK_FULL_NAME_PLURAL,
		xAxisTitle: (imperial: boolean) =>
			strings.PILLAR_RISK_XAXIS_TITLE.with({
				unit: imperial
					? strings.UNIT_62_MILES_SHORT
					: strings.UNIT_100_KILOMETERS_SHORT,
			}),
		tooltip: {
			title: strings.PILLAR_RISK_TOOLTIP_TITLE,
			copy: strings.PILLAR_RISK_TOOLTIP_COPY,
			fleet: strings.PILLAR_RISK_TOOLTIP_FLEET_COPY,
			user: strings.PILLAR_RISK_TOOLTIP_DRIVER_COPY,
		},
	},
	data: {
		averageBy: "mileage",
		key: "riskPer100",
		stat: "riskPer100",
		benchmarkStat: "risk",
	},
};

export const PILLAR_SPEED = {
	type: "speed",
	id: "speeding",
	icon: IconSpeed,
	calc: (value: any) => value.total,
	strings: {
		name: strings.PILLAR_SPEED_NAME,
		benchmarkTooltip: strings.PILLAR_BENCHMARK_TOOLTIP,
		description: (imperial: boolean) =>
			strings.PILLAR_SPEED_DESCRIPTION.with({
				unit: imperial
					? strings.UNIT_62_MILES_SHORT
					: strings.UNIT_100_KILOMETERS_SHORT,
			}),
		unitPlural: "%",
		unit: "%",
		suffix: "%",
		fullName: strings.PILLAR_SPEED_FULL_NAME,
		fullNamePlural: strings.PILLAR_SPEED_FULL_NAME_PLURAL,
		xAxisTitle: (imperial: boolean) =>
			strings.PILLAR_SPEED_XAXIS_TITLE.with({
				unit: imperial
					? strings.UNIT_62_MILES_SHORT
					: strings.UNIT_100_KILOMETERS_SHORT,
			}),
		tooltip: {
			title: strings.PILLAR_SPEED_TOOLTIP_TITLE,
			copy: strings.PILLAR_SPEED_TOOLTIP_COPY,
			fleet: strings.PILLAR_SPEED_TOOLTIP_FLEET_COPY,
			user: strings.PILLAR_SPEED_TOOLTIP_DRIVER_COPY,
		},
	},
	data: {
		averageBy: "engineEvents",
		key: "speedingPer100",
		boxplotKey: "percentSpeeding",
		stat: "percentSpeeding",
		benchmarkStat: "speed",
	},
};

export const PILLAR_DISTRACTION = {
	type: "distraction",
	id: "distractions",
	icon: IconDistraction,
	calc: per100KM,
	strings: {
		name: strings.PILLAR_DISTRACTION_NAME,
		benchmarkTooltip: strings.PILLAR_BENCHMARK_TOOLTIP,
		description: (imperial: boolean) =>
			strings.PILLAR_DISTRACTION_DESCRIPTION.with({
				unit: imperial
					? strings.UNIT_62_MILES_SHORT
					: strings.UNIT_100_KILOMETERS_SHORT,
			}),
		unitPlural: strings.PILLAR_UNIT_EVENT_PLURAL,
		unit: strings.PILLAR_UNIT_EVENT,
		suffix: "",
		fullName: strings.PILLAR_DISTRACTION_FULL_NAME,
		fullNamePlural: strings.PILLAR_DISTRACTION_FULL_NAME_PLURAL,
		xAxisTitle: (imperial: boolean) =>
			strings.PILLAR_DISTRACTION_XAXIS_TITLE.with({
				unit: imperial
					? strings.UNIT_62_MILES_SHORT
					: strings.UNIT_100_KILOMETERS_SHORT,
			}),
		tooltip: {
			title: strings.PILLAR_DISTRACTION_TOOLTIP_TITLE,
			copy: strings.PILLAR_DISTRACTION_TOOLTIP_COPY,
			fleet: strings.MISC_EMPTY_STRING,
			user: strings.MISC_EMPTY_STRING,
		},
	},
	data: {
		averageBy: "mileage",
		key: "distractionPer100",
		stat: "distractionPer100",
		benchmarkStat: "distractions",
	},
};

export const PILLAR_FATIGUE = {
	type: "fatigue",
	id: "fatigue",
	icon: IconFatigue,
	learning: {},
	calc: per100KM,
	strings: {
		name: strings.PILLAR_FATIGUE_NAME,
		benchmarkTooltip: strings.PILLAR_BENCHMARK_TOOLTIP,
		description: (imperial: boolean) =>
			strings.PILLAR_FATIGUE_DESCRIPTION.with({
				unit: imperial
					? strings.UNIT_62_MILES_SHORT
					: strings.UNIT_100_KILOMETERS_SHORT,
			}),
		unitPlural: null,
		unit: null,
		suffix: strings.PILLAR_FATIGUE_UNIT.map((unit) => ` ${unit}`),
		fullName: strings.PILLAR_FATIGUE_FULL_NAME,
		fullNamePlural: strings.PILLAR_FATIGUE_FULL_NAME_PLURAL,
		xAxisTitle: (imperial: boolean) =>
			strings.PILLAR_FATIGUE_XAXIS_TITLE.with({
				unit: imperial
					? strings.UNIT_62_MILES_SHORT
					: strings.UNIT_100_KILOMETERS_SHORT,
			}),
		tooltip: {
			title: strings.PILLAR_FATIGUE_TOOLTIP_TITLE,
			copy: strings.PILLAR_FATIGUE_TOOLTIP_COPY,
			fleet: strings.PILLAR_FATIGUE_TOOLTIP_FLEET_COPY,
			user: strings.PILLAR_FATIGUE_TOOLTIP_DRIVER_COPY,
		},
	},
	data: {
		averageBy: "mileage",
		key: "fatiguePer100",
		stat: "fatigue",
		benchmarkStat: "fatigue",
	},
};
const harsAccelerationData = isFiveKeysTheme()
	? {
			averageBy: "mileage",
			key: "accelerationPer100",
			stat: "accelerationPer100",
			benchmarkStat: "accelerationPer100",
	  }
	: {
			averageBy: "mileage",
			key: "accelerationPer100",
			stat: "eco",
			benchmarkStat: "eco",
	  };

export const PILLAR_ECO_HARSH_ACCELERATION = {
	type: "harshAcceleration",
	id: "acceleration",
	icon: IconHarshAcceleration,
	calc: per100KM,
	strings: {
		name: strings.SUB_PILLAR_HARSH_ACCELERATION_NAME,
		description: (imperial: boolean) =>
			strings.SUB_PILLAR_HARSH_ACCELERATION_DESCRIPTION.with({
				unit: imperial
					? strings.UNIT_62_MILES_SHORT
					: strings.UNIT_100_KILOMETERS_SHORT,
			}),
		suffix: "",
		xAxisTitle: (imperial: boolean) => {
			return strings.SUB_PILLAR_HARSH_ACCELERATION_XAXIS_TITLE.with({
				unit: imperial
					? strings.UNIT_62_MILES_SHORT
					: strings.UNIT_100_KILOMETERS_SHORT,
			});
		},
		fullName: strings.SUB_PILLAR_HARSH_ACCELERATION_FULL_NAME,
		fullNamePlural: strings.SUB_PILLAR_HARSH_ACCELERATION_FULL_NAME_PLURAL,
		tooltip: {
			title: strings.SUB_PILLAR_HARSH_ACCELERATION_NAME,
			copy: strings.PILLAR_ECO_TOOLTIP_COPY,
			fleet: strings.MISC_EMPTY_STRING,
			user: strings.MISC_EMPTY_STRING,
		},
	},
	data: harsAccelerationData,
};

const harsBrakingData = isFiveKeysTheme()
	? {
			averageBy: "mileage",
			key: "brakingPer100",
			stat: "brakingPer100",
			benchmarkStat: "brakingPer100",
	  }
	: {
			averageBy: "mileage",
			key: "brakingPer100",
			stat: "eco",
			benchmarkStat: "eco",
	  };

export const PILLAR_ECO_HARSH_BRAKING = {
	type: "harshBraking",
	id: "braking",
	icon: IconHarshBraking,
	calc: per100KM,
	strings: {
		name: strings.SUB_PILLAR_HARSH_BRAKING_NAME,
		description: (imperial: boolean) =>
			strings.SUB_PILLAR_HARSH_BRAKING_DESCRIPTION.with({
				unit: imperial
					? strings.UNIT_62_MILES_SHORT
					: strings.UNIT_100_KILOMETERS_SHORT,
			}),
		suffix: "",
		xAxisTitle: (imperial: boolean) => {
			return strings.SUB_PILLAR_HARSH_BRAKING_XAXIS_TITLE.with({
				unit: imperial
					? strings.UNIT_62_MILES_SHORT
					: strings.UNIT_100_KILOMETERS_SHORT,
			});
		},
		fullName: strings.SUB_PILLAR_HARSH_BRAKING_FULL_NAME,
		fullNamePlural: strings.SUB_PILLAR_HARSH_BRAKING_FULL_NAME_PLURAL,
		tooltip: {
			title: strings.SUB_PILLAR_HARSH_BRAKING_NAME,
			copy: strings.SUB_PILLAR_HARSH_BRAKING_TOOLTIP_COPY,
			fleet: strings.MISC_EMPTY_STRING,
			user: strings.MISC_EMPTY_STRING,
		},
	},
	data: harsBrakingData,
};

export const PILLAR_ECO = {
	type: "eco",
	id: "eco",
	icon: IconEco,
	calc: per100KM,
	strings: {
		name: strings.PILLAR_ECO_NAME,
		benchmarkTooltip: strings.PILLAR_BENCHMARK_TOOLTIP_EVENTS,
		description: (imperial: boolean) =>
			strings.PILLAR_ECO_DESCRIPTION.with({
				unit: imperial
					? strings.UNIT_62_MILES_SHORT
					: strings.UNIT_100_KILOMETERS_SHORT,
			}),
		unitPlural: strings.PILLAR_UNIT_EVENT_PLURAL,
		unit: strings.PILLAR_UNIT_EVENT,
		suffix: "",
		xAxisTitle: (imperial: boolean) =>
			strings.PILLAR_ECO_XAXIS_TITLE.with({
				unit: imperial
					? strings.UNIT_62_MILES_SHORT
					: strings.UNIT_100_KILOMETERS_SHORT,
			}),
		fullName: strings.PILLAR_ECO_FULL_NAME,
		fullNamePlural: strings.PILLAR_ECO_FULL_NAME_PLURAL,
		tooltip: {
			title: strings.PILLAR_ECO_TOOLTIP_TITLE,
			copy: strings.PILLAR_ECO_TOOLTIP_COPY,
			fleet: strings.MISC_EMPTY_STRING,
			user: strings.MISC_EMPTY_STRING,
		},
		viewSubPillars: strings.PILLAR_ECO_VIEW_BREAKDOWN,
	},
	data: {
		averageBy: "mileage",
		key: "ecoPer100",
		stat: "ecoPer100",
		benchmarkStat: "eco",
	},
	subPillars: [PILLAR_ECO_HARSH_ACCELERATION, PILLAR_ECO_HARSH_BRAKING],
};

export const PILLARS = [
	PILLAR_RISK,
	PILLAR_SPEED,
	PILLAR_DISTRACTION,
	PILLAR_FATIGUE,
	PILLAR_ECO,
];

export const FIVE_KEYS_PILLARS = [
	PILLAR_RISK,
	PILLAR_SPEED,
	PILLAR_DISTRACTION,
	PILLAR_FATIGUE,
	PILLAR_ECO_HARSH_ACCELERATION,
	PILLAR_ECO_HARSH_BRAKING,
];

export const PILLARS_WITH_SCORE = [DRIVING_SCORE, ...PILLARS];
export const FIVE_KEYS_PILLARS_WITH_SCORE = [
	DRIVING_SCORE,
	...FIVE_KEYS_PILLARS,
];
